import { FIELD_MODULUS, MAX_AMOUNT, TESTNET_NETWORK_ID, WalletError, type WalletDomain } from "./types";

export const utf8 = (value: string): Uint8Array => new TextEncoder().encode(value);
export const hex = (bytes: Uint8Array): string => Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
export function unhex(value: string, length?: number): Uint8Array {
  if (typeof value !== "string" || !/^(?:[0-9a-f]{2})*$/.test(value) || (length !== undefined && value.length !== length * 2)) {
    throw new WalletError("INVALID_ENCODING", "Expected canonical lowercase hexadecimal data.");
  }
  return Uint8Array.from(value.match(/../g) ?? [], byte => Number.parseInt(byte, 16));
}
export function field(value: string, nonzero = true): string {
  unhex(value, 32);
  const parsed = BigInt(`0x${value}`);
  if (parsed >= FIELD_MODULUS || (nonzero && parsed === 0n)) throw new WalletError("INVALID_FIELD", "Invalid field element.");
  return value;
}
export function domain(input: WalletDomain): WalletDomain {
  if (!input || typeof input !== "object") throw new WalletError("INVALID_DOMAIN", "Wallet domain is missing.");
  for (const key of ["networkId", "poolId", "assetId"] as const) unhex(input[key], 32);
  if (input.networkId !== TESTNET_NETWORK_ID || /^0+$/.test(input.poolId) || /^0+$/.test(input.assetId)) {
    throw new WalletError("INVALID_DOMAIN", "This wallet supports an explicitly pinned Stellar Testnet pool and asset only.");
  }
  return { networkId: input.networkId, poolId: input.poolId, assetId: input.assetId };
}
export function sameDomain(a: WalletDomain, b: WalletDomain): boolean {
  return a.networkId === b.networkId && a.poolId === b.poolId && a.assetId === b.assetId;
}
export function cryptoApi(): Crypto {
  const api = globalThis.crypto;
  if (("isSecureContext" in globalThis && globalThis.isSecureContext === false) || !api?.subtle || !api.getRandomValues) {
    throw new WalletError("CRYPTO_UNAVAILABLE", "A secure browser context with Web Crypto is required.");
  }
  return api;
}
export function randomBytes(length: number): Uint8Array {
  try { return cryptoApi().getRandomValues(new Uint8Array(length)); }
  catch { throw new WalletError("CRYPTO_UNAVAILABLE", "Secure randomness is unavailable."); }
}
export function randomField(spending = false): string {
  for (let attempts = 0; attempts < 128; attempts += 1) {
    const bytes = randomBytes(32);
    if (spending) bytes[0] &= 3;
    const result = hex(bytes);
    const value = BigInt(`0x${result}`);
    bytes.fill(0);
    if (value > 0n && value < (spending ? 1n << 250n : FIELD_MODULUS)) return result;
  }
  throw new WalletError("CRYPTO_UNAVAILABLE", "Secure random field sampling failed.");
}
export function concat(...values: Uint8Array[]): Uint8Array {
  const output = new Uint8Array(values.reduce((sum, bytes) => sum + bytes.length, 0));
  let offset = 0;
  for (const bytes of values) { output.set(bytes, offset); offset += bytes.length; }
  return output;
}
export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await cryptoApi().subtle.digest("SHA-256", bytes as BufferSource));
}
export function amount(value: string, allowZero = false): bigint {
  if (typeof value !== "string" || !/^(0|[1-9][0-9]{0,18})$/.test(value)) throw new WalletError("INVALID_AMOUNT", "Amount must be an exact decimal stroop integer.");
  const result = BigInt(value);
  if (result > MAX_AMOUNT || (!allowZero && result === 0n)) throw new WalletError("INVALID_AMOUNT", "Amount is outside the supported 63-bit range.");
  return result;
}
export function parseXlm(value: string): bigint {
  if (!/^(0|[1-9][0-9]*)(?:\.([0-9]{1,7}))?$/.test(value)) throw new WalletError("INVALID_AMOUNT", "Use at most seven decimal places without rounding.");
  const [whole, fraction = ""] = value.split(".");
  return amount((BigInt(whole) * 10000000n + BigInt(fraction.padEnd(7, "0"))).toString());
}
export function formatXlm(stroops: bigint | string): string {
  const value = amount(typeof stroops === "bigint" ? stroops.toString() : stroops, true);
  return `${value / 10000000n}.${(value % 10000000n).toString().padStart(7, "0")}`;
}
export function safeIndex(value: number, min = 0, max = 65535): number {
  if (!Number.isSafeInteger(value) || value < min || value > max) throw new WalletError("INVALID_STATE", "Invalid public state index.");
  return value;
}
export function validId(value: string): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9_-]{8,128}$/.test(value)) throw new WalletError("INVALID_ID", "Invalid operation identifier.");
  return value;
}
export const freshId = (): string => hex(randomBytes(16));
